import {
  useQueries,
  type QueryFunctionContext,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  dataApiClient,
  type AgentAggregationDimension,
  type TripAggregationDimension,
} from '../../infrastructure/dataApi/client';
import type { Aggregation } from '../../types/domain';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useMapVisibility } from '../SimViewer/useMapVisibility';
import { useAggregationConfig } from '../SimViewer/useAggregationConfig';
import { useSchemaComparison } from '../SimViewer/useSchemaComparison';

type AreaAggregationDimension = Extract<
  AgentAggregationDimension | TripAggregationDimension,
  'home' | 'work' | 'start' | 'end'
>;

type UseAreaAggregationComparisonsData = {
  isLoading: boolean;
  aggregationsMap: Record<string, Map<string, number>>;
};

const EMPTY_DATA = new Map<string, never>();

const useAreaAggregationComparisonsData = (
  dimension: AreaAggregationDimension
): UseAreaAggregationComparisonsData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { comparisons } = useSchemaComparison();
  const { apiFilter } = useApiDataFilter();
  const { visibleAreaType } = useMapVisibility();
  const { aggregationConfig } = useAggregationConfig();

  const simDataIds = currentSimDataId ? [currentSimDataId, ...comparisons] : [];
  const areaType =
    aggregationConfig?.type === 'area_pref' ? 'prefecture' : visibleAreaType;

  const results = useQueries({
    queries: simDataIds.map((simDataId: string) => {
      return {
        queryKey: [
          'areaAggregation',
          simDataId,
          apiFilter,
          'count_per_area',
          dimension,
          areaType,
        ],
        queryFn: async ({
          signal,
        }: QueryFunctionContext): Promise<Map<string, number>> => {
          switch (dimension) {
            case 'home':
            case 'work':
              return dataApiClient
                .getAgentAggr(
                  simDataId,
                  apiFilter,
                  'count_per_area',
                  dimension,
                  areaType,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map<string, number>(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
            case 'start':
            case 'end':
              return dataApiClient
                .getTripAggr(
                  simDataId,
                  apiFilter,
                  'count_per_area',
                  dimension,
                  areaType,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map<string, number>(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
          }
        },
        meta: { dataType: 'others' },
      };
    }),
  });

  return {
    isLoading: results.some((result: UseQueryResult): boolean => {
      return result.isFetching;
    }),
    aggregationsMap: Object.fromEntries(
      simDataIds.map(
        (simDataId: string, index: number): [string, Map<string, number>] => {
          return [simDataId, results[index].data ?? EMPTY_DATA];
        }
      )
    ),
  };
};

export { useAreaAggregationComparisonsData };
